import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/news.png';
import { Database, Zap, Globe, Clock, TrendingUp, Activity } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/news');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/news-narrative-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/news-impact-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/chat-bubble');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/filing-language-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);


const StocksPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Financial News API – Stock & Market News Data"
        description="Financial news API with stock and market news from 500+ sources, entity extraction and impact scoring: company headlines and decades of history."
        canonicalUrl="https://axionquant.com/products/news"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Financial News API",
          "description": "Financial news API for finance – stock and market news data from 500+ sources with entity extraction and impact scoring. Company news API with up-to-date headlines and decades of history.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Financial News API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>

      <Header
  docs="/docs/04-macros-and-signals/news"
  title="Financial News API – Stock & Market News Data"
  body="Stock news API and market news API in one feed - news aggregation, entity extraction, and impact scoring that turns unstructured headlines into a queryable, backtestable financial news dataset."
  crumb={{name: 'News', path:'/products/news'}}
  tabs={[
    {
      label: 'Company News Monitor',
      language: 'python',
      filename: 'company_news_monitor.py',
      bg: '6.png',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")

watchlist = ["NVDA", "AMD", "INTC", "TSM"]

coverage = {
    ticker: client.news.company(ticker)
    for ticker in watchlist
}

article_counts = {
    ticker: len(articles)
    for ticker, articles in coverage.items()
}

latest = {
    ticker: articles[0]["title"]
    for ticker, articles in coverage.items()
    if articles
}
`,
    },
    {
      label: 'Macro News by Region',
      language: 'python',
      filename: 'macro_news_by_region.py',
      bg: '2.png',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")

regions = ["US", "GB", "JP", "DE", "CN"]

regional_feed = {
    country: client.news.country(country)
    for country in regions
}

headlines_by_region = {
    country: [a["title"] for a in articles[:3]]
    for country, articles in regional_feed.items()
}

volume_by_region = {
    country: len(articles)
    for country, articles in regional_feed.items()
}
`,
    },
    {
      label: 'Sentiment Cross-Signal',
      language: 'python',
      filename: 'sentiment_cross_signal.py',
      bg: '8.png',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")

tickers = ["TSLA", "AMZN", "META", "MSFT"]

signals = {
    ticker: {
        "social":  client.sentiment.social(ticker),
        "news":    client.sentiment.news(ticker),
        "analyst": client.sentiment.analyst(ticker),
    }
    for ticker in tickers
}

scored = [
    {
        "ticker":   ticker,
        "social":   data["social"]["score"],
        "news":     data["news"]["score"],
        "analyst":  data["analyst"]["score"],
        "net_buys": data["analyst"]["strongBuy"] + data["analyst"]["buy"],
    }
    for ticker, data in signals.items()
]

ranked = sorted(scored, key=lambda x: x["news"], reverse=True)
`,
    },
    {
      label: 'Sector News Breakdown',
      language: 'python',
      filename: 'sector_news_breakdown.py',
      bg: '4.png',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")

categories = ["technology", "energy", "markets", "crypto"]

sector_feed = {
    category: client.news.category(category)
    for category in categories
}

publishers_by_sector = {
    category: list({
        a["publisher"]
        for a in articles
    })
    for category, articles in sector_feed.items()
}

volume_by_sector = {
    category: len(articles)
    for category, articles in sector_feed.items()
}
`,
    },
  ]}
/>
      <Marquee/>

      <FeaturesGrid
        title="Financial News Intelligence. "
        subtitle="Stock and market news data with entity extraction. "
        features={ [
        {
          fig: 'FIG 0.1',
          icon: <Zap size={80} color="var(--purple)" />,
          title: 'Daily and Hourly Updates',
          description: 'Up-to-date headlines from 500+ global sources refreshed daily and hourly, with decades of history retrievable nearly instantly.',
        },
        {
          fig: 'FIG 0.2',
          icon: <Clock size={80} color="var(--green)" />,
          title: 'Deep Historical Archive',
          description: '2 billion+ articles stored with millisecond-precise timestamps. Backtest event-driven strategies on 35+ years of financial news.',
        },
        {
          fig: 'FIG 0.3',
          icon: <Database size={80} color="var(--red)" />,
          title: 'Entity Extraction',
          description: 'Tagged companies, people, organisations and topics. Every article enriched with 15+ entity tags for instant filtering.',
        },
        {
          fig: 'FIG 0.4',
          icon: <TrendingUp size={80} color="var(--purple)" />,
          title: 'Impact Scoring',
          description: 'Proprietary models score each article for market relevance and potential price impact. Focus on what really moves markets.',
        },
        {
          fig: 'FIG 0.5',
          icon: <Activity size={80} color="var(--green)" />,
          title: 'Sentiment Analytics',
          description: 'Granular sentiment signals extracted from text. Track sentiment trends, detect shifts, and correlate with asset returns.',
        },
        {
          fig: 'FIG 0.6',
          icon: <Globe size={80} color="var(--red)" />,
          title: 'Global Multilingual Coverage',
          description: 'News in 40+ languages from North America, Europe, Asia and emerging markets. Unified API with translation and normalization across the full archive.',
        },
      ]}/>



      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="News Intelligence Platform"
        headingPlain="One unified news feed"
        headingEm="across 500+ sources."
        body="Institutional-grade financial news aggregation with structured data and impact scoring - normalized, timestamped, and ready for production models."
        stats={[
          { v: "500+",      l: "Sources"           },
          { v: "30+",       l: "Languages"         },
          { v: "2B+",       l: "Articles Archived" },
          { v: "99.95%",    l: "Uptime SLA"        },
        ]}
        markers={[
          { id: "nyc",        location: [40.71, -74.01],   label: "New York" },
          { id: "lon",        location: [51.51, -0.09],    label: "London" },
          { id: "tokyo",      location: [35.68, 139.77],   label: "Tokyo" },
          { id: "hk",         location: [22.28, 114.16],   label: "Hong Kong" },
          { id: "sydney",     location: [-33.87, 151.21],  label: "Sydney" },

          { id: "washington", location: [38.90, -77.04],   label: "Washington DC" },
          { id: "losangeles", location: [34.05, -118.24],  label: "Los Angeles" },
          { id: "sanfrancisco", location: [37.77, -122.42], label: "San Francisco" },
          { id: "chicago",    location: [41.88, -87.63],   label: "Chicago" },
          { id: "toronto",    location: [43.65, -79.38],   label: "Toronto" },

          { id: "paris",      location: [48.86, 2.35],     label: "Paris" },
          { id: "berlin",     location: [52.52, 13.40],    label: "Berlin" },
          { id: "frankfurt",  location: [50.11, 8.68],     label: "Frankfurt" },
          { id: "rome",       location: [41.90, 12.50],    label: "Rome" },
          { id: "madrid",     location: [40.42, -3.70],    label: "Madrid" },
          { id: "amsterdam",  location: [52.37, 4.90],     label: "Amsterdam" },
          { id: "brussels",   location: [50.85, 4.35],     label: "Brussels" },
          { id: "zurich",     location: [47.38, 8.54],     label: "Zurich" },
          { id: "moscow",     location: [55.75, 37.62],    label: "Moscow" },
          { id: "istanbul",   location: [41.01, 28.97],    label: "Istanbul" },

          { id: "beijing",    location: [39.90, 116.40],   label: "Beijing" },
          { id: "shanghai",   location: [31.23, 121.47],   label: "Shanghai" },
          { id: "seoul",      location: [37.57, 126.98],   label: "Seoul" },
          { id: "singapore",  location: [1.29, 103.85],    label: "Singapore" },
          { id: "mumbai",     location: [19.08, 72.88],    label: "Mumbai" },
          { id: "delhi",      location: [28.61, 77.21],    label: "New Delhi" },
          { id: "bangkok",    location: [13.75, 100.50],   label: "Bangkok" },
          { id: "jakarta",    location: [-6.21, 106.85],   label: "Jakarta" },
          { id: "taipei",     location: [25.03, 121.57],   label: "Taipei" },
          { id: "manila",     location: [14.60, 120.98],   label: "Manila" },

          { id: "dubai",      location: [25.20, 55.27],    label: "Dubai" },
          { id: "riyadh",     location: [24.71, 46.67],    label: "Riyadh" },
          { id: "doha",       location: [25.29, 51.53],    label: "Doha" },
          { id: "telaviv",    location: [32.08, 34.78],    label: "Tel Aviv" },
          { id: "cairo",      location: [30.04, 31.24],    label: "Cairo" },
          { id: "johannesburg", location: [-26.20, 28.05], label: "Johannesburg" },
          { id: "lagos",      location: [6.52, 3.38],      label: "Lagos" },
          { id: "nairobi",    location: [-1.29, 36.82],    label: "Nairobi" },

          { id: "sao-paulo",  location: [-23.55, -46.63],  label: "São Paulo" },
          { id: "mexico-city",location: [19.43, -99.13],   label: "Mexico City" },
          { id: "buenosaires",location: [-34.60, -58.38],  label: "Buenos Aires" },
          { id: "santiago",   location: [-33.45, -70.66],  label: "Santiago" },
          { id: "bogota",     location: [4.71, -74.07],    label: "Bogotá" },
          { id: "lima",       location: [-12.05, -77.04],  label: "Lima" },

          { id: "auckland",   location: [-36.85, 174.76],  label: "Auckland" },
          { id: "melbourne",  location: [-37.81, 144.96],  label: "Melbourne" },
          { id: "perth",      location: [-31.95, 115.86],  label: "Perth" },

          { id: "oslo",       location: [59.91, 10.75],    label: "Oslo" },
          { id: "stockholm",  location: [59.33, 18.07],    label: "Stockholm" },
          { id: "helsinki",   location: [60.17, 24.94],    label: "Helsinki" },
          { id: "copenhagen", location: [55.68, 12.57],    label: "Copenhagen" },
          { id: "vienna",     location: [48.21, 16.37],    label: "Vienna" },
          { id: "prague",     location: [50.08, 14.43],    label: "Prague" },

          { id: "geneva",     location: [46.20, 6.14],     label: "Geneva" },
          { id: "warsaw",     location: [52.23, 21.01],    label: "Warsaw" },
          { id: "athens",     location: [37.98, 23.73],    label: "Athens" },
        ]}
      />

      <DataGrid
        title="Financial News API: Structured, Searchable, Actionable"
        subtitle="Don't just read the news - analyze it. Our platform transforms global market news flow into a structured, entity-tagged database. Filter, backtest, and model the market impact of every story."
        bg={databg}
        stats={[
          { label: 'Global News Sources', value: '500+' },
          { label: 'Articles Archived', value: '2B+' },
          { label: 'Entity Tags per Article', value: '15+' },
        ]}
      />
      <CardList
        title="From Headlines to Alpha with News Data"
        vid={'https://cdn.studio14a.com/axion/products/cards/news.mp4'}
        points={[
          {
            "title": "Comprehensive News Archive & API",
            "details": "Access a vast, historical archive of financial news articles from financial publishers, global newspapers, trade journals, and regulatory newswires with millisecond-precise timestamps.",
            "cta": "Search the news archive",
            "url": "/docs"
          },
          {
            "title": "Smart Tagging & Entity Recognition",
            "details": "Every article is automatically tagged with relevant companies, people, organizations, and topics. Instantly filter for news impacting your portfolio or specific investment thesis.",
            "cta": "Explore entity tagging",
            "url": "/products/sentiment"
          },
          {
            "title": "Current News Coverage & Filters",
            "details": "Integrate current daily and hourly news coverage directly into your models and research workflows. Configure custom filters for specific entities, keywords, or sentiment thresholds.",
            "cta": "Set up news alerts",
            "url": "/docs"
          }
        ]}
      />

      <Responses
        heading="Research market-moving headlines"
        subheading="One API surfaces up-to-date headlines, sentiment scores, and global coverage for any ticker, structured and scored, with decades of history retrievable nearly instantly."
        ticker="AAPL"
        tabs={[
          {
            title: 'Company News',
            description: 'Current headlines tied directly to a ticker, pulled from financial and general news sources and ready to feed straight into your pipeline.',
            data: [
              {
                title: "Apple spent 2025 setting itself up for the future - and its biggest moves weren't about AI - Yahoo Finance",
                link: "https://api.axionquant.com/news/article/aHR0cHM6Ly9uZXdzLmdvb2dsZS5jb20vcnNzL2FydGljbGVzL0NCTWl6d0ZCVlY5NWNVeE5OblZDV1VwbVMxTnhaVlZRYVd4VlRqQk9XR1ZOTWxFM1UxaFdiRjlXYld4elNDMWlVVk5IYWxKT1pFbGhWbmc1TlZKRlEycHJNMHBKYjNGeGMyUXlPRTAzZGxGNFRGaFRiVUZWTUhveGRsRk9Va2M1YmxKV2R6RTFSbkV4VDBKV1pHTmtNMVJ6TVRsV00xaFZjbWRwYVRsUFJYbEdRbGRMU3pWMFVrZGZUVFJzZFdkWE9Vb3lUSGgyT0hZMlZWQTROM2R5YUhkb1pVczJiM0JLVVV0d2FuUk5RalpKTUZacVpITm9WamhXVEhOamVVaG9WMnhMZGt4dFUxWk1hV2szVlZaUlJ6bENhekE_b2M9NQ",
                summary: "Apple spent 2025 setting itself up for the future - and its biggest moves weren't about AI  Yahoo Finance",
                published: "Wed, 24 Dec 2025 09:05:43 GMT"
              },
              {
                title: "Apple Just Released a New AI Model. Should You Buy AAPL Stock Here? - Barchart.com",
                link: "https://api.axionquant.com/news/article/aHR0cHM6Ly9uZXdzLmdvb2dsZS5jb20vcnNzL2FydGljbGVzL0NCTWlyd0ZCVlY5NWNVeE9aRVV6TkZoTWJqbFpiRzV2YTI4MlFsUnFjMGhxVldSbU0waFZWek5SU0d4WlRsRkdaRWg0YW05Rk9IcFBUbFE0WTNJMlNDMUxOMk5oYjJ4SGEzQktNMGhqUmpkd2NWOUVWelpWY3pCZmFWcHVhemhFVTA5RFpuSlBlbkJEV0hGV2FtNUxXV1ZaTW1GUE9GWkljSGxzU0hWNGFrZHFiVk41U2psQ2RFOXplRk52YmpVM1dXSTJTa3hWWVZZd1VsVmZTM1pCY21wR05ubHdTWEY2T1RneGIwbHBNVWhLYVZWSj9vYz01",
                summary: "Apple Just Released a New AI Model. Should You Buy AAPL Stock Here?  Barchart.com",
                published: "Tue, 23 Dec 2025 16:00:03 GMT"
              }
            ],
          },
          {
            title: 'News Sentiment',
            description: 'Aggregate sentiment scored across every article and press release for a ticker. Quantify how the market narrative shifts over days, quarters, and decades.',
            data: {
              label: "NEGATIVE",
              score: 0.982357367873192,
              breakdown: {
                positive: {
                  count: 231,
                  avgScore: 0.903340172767639
                },
                negative: {
                  count: 1292,
                  avgScore: 0.982357367873192
                }
              }
            },
          },
          {
            title: 'Sector News',
            description: 'Filter headlines by category - technology, business, healthcare, and more - to track the stories moving your specific corner of the market.',
            data: [
              {
                title: "Apple Just Released a New AI Model. Should You Buy AAPL Stock Here? - Barchart.com",
                link: "https://api.axionquant.com/news/article/aHR0cHM6Ly9uZXdzLmdvb2dsZS5jb20vcnNzL2FydGljbGVzL0NCTWlyd0ZCVlY5NWNVeE9aRVV6TkZoTWJqbFpiRzV2YTI4MlFsUnFjMGhxVldSbU0waFZWek5SU0d4WlRsRkdaRWg0YW05Rk9IcFBUbFE0WTNJMlNDMUxOMk5oYjJ4SGEzQktNMGhqUmpkd2NWOUVWelpWY3pCZmFWcHVhemhFVTA5RFpuSlBlbkJEV0hGV2FtNUxXV1ZaTW1GUE9GWkljSGxzU0hWNGFrZHFiVk41U2psQ2RFOXplRk52YmpVM1dXSTJTa3hWWVZZd1VsVmZTM1pCY21wR05ubHdTWEY2T1RneGIwbHBNVWhLYVZWSj9vYz01",
                summary: "Apple Just Released a New AI Model. Should You Buy AAPL Stock Here?  Barchart.com",
                published: "Tue, 23 Dec 2025 16:00:03 GMT"
              }
            ],
          },
          {
            title: 'Global Coverage',
            description: 'Filter by country or category to track regional markets and macro shifts across the full archive, with current stories updated daily.',
            data: [
              {
                title: "Trump Live Updates: DOJ Says It Has Found 1 Million More Epstein Files - The New York Times",
                link: "https://api.axionquant.com/news/article/aHR0cHM6Ly9uZXdzLmdvb2dsZS5jb20vcnNzL2FydGljbGVzL0NCTWlZMEZWWDNseFRFOTFiMVIzWVhseE1YbE1TSGd3YW1sSFpFcFdiRmhTWjJWTWNYTmtlRmxQWkdGeGN6SlBXSFp3UzFwTExYTlViRGxYWlRKTFRUVjBiVmgwYlUxVWJtUjVla3BYVEhrd1dqaEdXV1l3YUVSSFVrcGxRVTU0YkdvMFFUVlNVUT9vYz01",
                summary: "Trump Live Updates: DOJ Says It Has Found 1 Million More Epstein Files  The New York Times\nNews Wrap: DOJ says over a million more Epstein documents discovered  PBS",
                published: "Wed, 24 Dec 2025 21:31:18 GMT"
              }
            ],
          },
          {
            title: 'Up-to-Date Feed',
            description: 'Current global news across every category, refreshed daily and hourly - the same coverage powering institutional research desks, available to anyone.',
            data: [
              {
                title: "Powerful winter storm arrives in Southern California for Christmas holiday. Here's what to know. - CBS News",
                link: "https://api.axionquant.com/news/article/aHR0cHM6Ly9uZXdzLmdvb2dsZS5jb20vcnNzL2FydGljbGVzL0NCTWl0QUZCVlY5NWNVeE9RbUowTTNGdE5XUnNXVFV4U2pKa09VWmZja2h2YWtoTmVHaDJSWFZUZVVwRGVIRkRRV2RVYlc5M1NYZEhMVGxEVUZoa1IxbDZOek5ZUWtscU1WTllWRU5YWVRKcGRtdFlXR1ZaUmtkZmNuVldXRTF6VjJGQlVVcFlSbDgyUjNKMlJtMUVVbUpGTFhoalRqbGhTemhTYldrM1VuUnpVRFEyT0ZGd0xVeFVZMlJKY0hvd2QwSkdVM0JHVWs5cmNUbGlXaTEwUm5abFREQnRURkJtU1V0Nk5tNU9PRGwxV2xoeGRVaGhTVVU_b2M9NQ",
                summary: "Powerful winter storm arrives in Southern California for Christmas holiday. Here's what to know.  CBS News\nStorm brings heavy rain, wind; Flash Flood Warning extended for parts of LA and Ventura counties  ABC7 Los Angeles",
                published: "Wed, 24 Dec 2025 14:52:00 GMT"
              }
            ],
          },
        ]}
      />
      <DatasetDetails full={true}
        title="Comprehensive Financial News Coverage"
        subtitle="Access up-to-date news coverage from over 500 financial news sources including major newswires, global newspapers, trade journals, and regulatory releases. Our news API for finance extracts 15+ entities per article, assigns market impact scores, and provides a searchable archive of 2 billion+ articles."
        cta="Try for Free"
        ctaLink="/login"
        tiles={[
          {
            img: '12.webp',
            title: 'We aggregate 500+ sources',
            caption: 'You get daily and hourly updates from major newswires, newspapers, trade journals, and regulatory releases.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '14.webp',
            title: 'We cover 40+ languages',
            caption: 'You can access and analyze financial news from every major market around the world.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '3.webp',
            title: 'We ingest 50,000+ articles daily',
            caption: 'You never miss critical news with our high-throughput content processing pipeline.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '0.webp',
            title: 'We archive 2B+ articles',
            caption: 'You can search and backtest event-driven strategies across billions of news articles.',
            cn: '',
            tile: <W4/>
          },
          {
            img: '1.webp',
            title: 'We retrieve in under 10 seconds',
            caption: 'You can pull decades of news history with millisecond-precise timestamps and sub-10s retrieval.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />
      
      <Outcomes
        headline="Numbers that speak for themselves."
        leftCell={{ stat: '500+', title: 'Global sources.', desc: 'Curated financial news providers.', showChart: true }}
        rightCells={[
          { stat: '2B+', title: 'Articles archived.', desc: 'Searchable with millisecond timestamps.' },
          { stat: '15+', title: 'Entity tags per article.', desc: 'Deep structural metadata for every story.', boldDesc: true },
        ]}

      /><ProductFAQ slug="news" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default StocksPage;
